import { useTranslation } from "react-i18next";
import personaDay from "@/assets/home/persona-day.webp";
import personaLongterm from "@/assets/home/persona-longterm.webp";
import personaPortfolio from "@/assets/home/persona-portfolio.webp";
import personaQuant from "@/assets/home/persona-quant.webp";
import personaSwing from "@/assets/home/persona-swing.webp";
import personaThematic from "@/assets/home/persona-thematic.webp";
import { BackdropVideo } from "@/components/marketing/BackdropVideo";
import { Section, SectionHeader } from "@/components/marketing/Section";
import { StaggeredList } from "@/components/ui/motion";

// Glass renders follow the persona order in `whoWeServe.personas`: hourglass
// (long-term), sine wave (swing), lightning (day), telescope (thematic),
// flask (quant/systematic), balance scale (portfolio).
const PERSONA_IMAGES = [
  personaLongterm,
  personaSwing,
  personaDay,
  personaThematic,
  personaQuant,
  personaPortfolio,
];

export function WhoWeServe() {
  const { t } = useTranslation();
  const personas = t("whoWeServe.personas", { returnObjects: true }) as {
    title: string;
    body: string;
  }[];

  return (
    <Section id="who-we-serve" viewport>
      {/* Lone figure before a vast swirling landscape — the individual trader
          facing the market. */}
      <BackdropVideo src="/videos/spiral-field-loop.mp4" />
      <div className="relative z-[1]">
        <SectionHeader title={t("whoWeServe.title")} subtitle={t("whoWeServe.subtitle")} />
        {/* Same card anatomy as "What we provide": glass render on top, copy below. */}
        <StaggeredList
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          staggerDelay={0.1}
        >
          {personas.map((persona, index) => (
            <div
              key={persona.title}
              className="glass-card flex h-full flex-col overflow-hidden rounded-[2rem]"
            >
              <div className="p-4 pb-0">
                <div className="relative aspect-[16/7] w-full overflow-hidden rounded-[1.5rem] bg-muted">
                  <img
                    src={PERSONA_IMAGES[index] ?? personaLongterm}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-6">
                <h3 className="text-base font-semibold text-foreground">{persona.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{persona.body}</p>
              </div>
            </div>
          ))}
        </StaggeredList>
      </div>
    </Section>
  );
}
